<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="video-uploader">
        <h2>Upload Videos</h2>
        
        <!-- Upload Form -->
        <div class="upload-form">
          <div class="upload-methods">
            <button 
              :class="['method-btn', { active: uploadMethod === 'single' }]" 
              @click="uploadMethod = 'single'"
            >
              Single Upload
            </button>
            <button 
              :class="['method-btn', { active: uploadMethod === 'multiple' }]" 
              @click="uploadMethod = 'multiple'"
            >
              Multiple Upload
            </button>
          </div>

          <!-- Single Upload Form -->
          <div v-if="uploadMethod === 'single'" class="single-upload">
            <div class="form-group">
              <label for="title">Video Title</label>
              <input 
                type="text" 
                id="title" 
                v-model="singleUpload.title" 
                placeholder="Enter video title"
              >
            </div>
            <div class="form-group">
              <label for="description">Description</label>
              <QuillEditor 
                id="description" 
                v-model:content="singleUpload.description" 
                label="Description"
                :toolbar="toolbarOptions"
                placeholder="Enter video description"
              />
            </div>
            <div class="form-group">
              <label for="file">Select Video</label>
              <input 
                type="file" 
                id="file" 
                @change="handleSingleFileSelect" 
                accept="video/*"
              >
            </div>
            <button 
              class="upload-btn" 
              @click="uploadSingle" 
              :disabled="!singleUpload.file || uploading"
            >
              {{ uploading ? 'Uploading...' : 'Upload Video' }}
            </button>
          </div>

          <!-- Multiple Upload Form -->
          <div v-else class="multiple-upload">
            <div 
              class="drop-zone"
              :class="{ 'drop-zone--over': isDragging }"
              @dragenter.prevent="isDragging = true"
              @dragleave.prevent="handleDragLeave"
              @dragover.prevent
              @drop.prevent="handleDrop"
            >
              <div class="drop-zone__content">
                <svg class="drop-zone__icon" xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p class="drop-zone__text">Drag & drop your videos here<br>or</p>
                <input 
                  type="file" 
                  id="files" 
                  @change="handleMultipleFileSelect" 
                  accept="video/*" 
                  multiple
                  class="drop-zone__input"
                >
                <label for="files" class="drop-zone__label">Browse Files</label>
              </div>
            </div>
            
            <!-- Selected Files List -->
            <div v-if="multipleUpload.files.length" class="selected-files">
              <h3>Selected Videos</h3>
              <ul>
                <li v-for="(file, index) in multipleUpload.files" :key="index" class="file-item">
                  <div class="file-info">
                    <span class="file-name">{{ file.name }}</span>
                    <div class="file-metadata">
                      <div class="input-group">
                        <label>Title:</label>
                        <input 
                          type="text" 
                          v-model="multipleUpload.titles[index]" 
                          :placeholder="'Enter title for ' + file.name"
                          class="file-input"
                        >
                      </div>
                      <div class="input-group">
                        <label>Description:</label>
                        <QuillEditor 
                          v-model:content="multipleUpload.descriptions[index]" 
                          :placeholder="'Enter description for ' + file.name"
                          :toolbar="toolbarOptions"
                          class="file-input file-description"
                        />
                      </div>
                    </div>
                  </div>
                  <button class="remove-btn" @click="removeFile(index)">Remove</button>
                </li>
              </ul>
              <button 
                class="upload-btn" 
                @click="uploadMultiple" 
                :disabled="uploading || !isValidForUpload"
              >
                {{ uploading ? 'Uploading...' : 'Upload All Videos' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Upload Progress -->
        <div v-if="uploadProgress.length" class="upload-progress">
          <h3>Upload Progress</h3>
          <ul>
            <li v-for="progress in uploadProgress" :key="progress.id" class="progress-item">
              <div class="progress-info">
                <span class="progress-title">{{ progress.title }}</span>
                <span class="progress-status">{{ progress.status }}</span>
              </div>
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: progress.progress + '%' }"
                  :class="{ error: progress.error }"
                ></div>
              </div>
              <span class="progress-percentage">{{ progress.progress }}%</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'VideoUploader',
  components: {
    Sidebar,
    QuillEditor
  },
  data() {
    return {
      uploadMethod: 'single',
      uploading: false,
      isDragging: false,
      dragCounter: 0,
      toolbarOptions: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link']
      ],
      singleUpload: {
        title: '',
        description: '',
        file: null
      },
      multipleUpload: {
        files: [],
        titles: [],
        descriptions: []
      },
      uploadProgress: [],
      accessToken: null
    };
  },
  computed: {
    isValidForUpload() {
      // Check if there are files and each file has at least a title
      return this.multipleUpload.files.length > 0 && 
             this.multipleUpload.files.every((_, index) => 
               this.multipleUpload.titles[index]?.trim() !== ''
             );
    }
  },
  methods: {
    async getAccessToken() {
      try {
        const authResponse = await axios.post('https://ws.api.video/auth/api-key', {
          apiKey: 'LXcWKZfW4Kn45Y9Z3v5IdNYl1btN228g0sSVSG2hAOL'
        }, {
          headers: {
            'accept': 'application/json',
            'content-type': 'application/json'
          }
        });
        this.accessToken = authResponse.data.access_token;
      } catch (err) {
        console.error('Error getting access token:', err);
        throw err;
      }
    },

    handleSingleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.singleUpload.file = file;
      }
    },

    handleMultipleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.multipleUpload.files = files;
      this.multipleUpload.titles = files.map(() => '');
      this.multipleUpload.descriptions = files.map(() => '');
    },

    removeFile(index) {
      this.multipleUpload.files.splice(index, 1);
      this.multipleUpload.titles.splice(index, 1);
      this.multipleUpload.descriptions.splice(index, 1);
    },

    async createVideo(title, description = '') {
      try {
        const response = await axios.post('https://ws.api.video/videos', {
          title,
          description
        }, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (err) {
        console.error('Error creating video:', err);
        throw err;
      }
    },

    async uploadVideoFile(videoId, file, progressCallback) {
      try {
        const chunkSize = 50 * 1024 * 1024; // 50MB chunks
        const totalChunks = Math.ceil(file.size / chunkSize);
        
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('file', chunk);

          await axios.post(`https://ws.api.video/videos/${videoId}/source`, formData, {
            headers: {
              'Authorization': `Bearer ${this.accessToken}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const totalProgress = ((i * chunkSize + progressEvent.loaded) / file.size) * 100;
              progressCallback(Math.min(totalProgress, 100));
            }
          });
        }
      } catch (err) {
        console.error('Error uploading video:', err);
        throw err;
      }
    },

    async uploadSingle() {
      if (!this.singleUpload.file || !this.singleUpload.title) return;

      this.uploading = true;
      const progressId = Date.now();
      this.uploadProgress.push({
        id: progressId,
        title: this.singleUpload.title,
        progress: 0,
        status: 'Preparing upload...',
        error: false
      });

      try {
        if (!this.accessToken) {
          await this.getAccessToken();
        }

        const video = await this.createVideo(
          this.singleUpload.title,
          this.singleUpload.description
        );

        await this.uploadVideoFile(video.videoId, this.singleUpload.file, (progress) => {
          const progressItem = this.uploadProgress.find(p => p.id === progressId);
          if (progressItem) {
            progressItem.progress = Math.round(progress);
            progressItem.status = progress === 100 ? 'Upload complete!' : 'Uploading...';
          }
        });

        // Reset form
        this.singleUpload = {
          title: '',
          description: '',
          file: null
        };
        
        // Reset file input
        const fileInput = document.getElementById('file');
        if (fileInput) fileInput.value = '';

      } catch (err) {
        const progressItem = this.uploadProgress.find(p => p.id === progressId);
        if (progressItem) {
          progressItem.status = `Error: ${err.message}`;
          progressItem.error = true;
        }
      } finally {
        this.uploading = false;
      }
    },

    async uploadMultiple() {
      if (!this.multipleUpload.files.length) return;

      this.uploading = true;
      const uploads = this.multipleUpload.files.map((file, index) => ({
        id: Date.now() + index,
        file,
        title: this.multipleUpload.titles[index] || file.name,
        description: this.multipleUpload.descriptions[index] || ''
      }));

      // Initialize progress for all files
      uploads.forEach(upload => {
        this.uploadProgress.push({
          id: upload.id,
          title: upload.title,
          progress: 0,
          status: 'Waiting...',
          error: false
        });
      });

      try {
        if (!this.accessToken) {
          await this.getAccessToken();
        }

        // Upload files sequentially
        for (const upload of uploads) {
          const progressItem = this.uploadProgress.find(p => p.id === upload.id);
          progressItem.status = 'Preparing upload...';

          try {
            const video = await this.createVideo(upload.title, upload.description);
            await this.uploadVideoFile(video.videoId, upload.file, (progress) => {
              progressItem.progress = Math.round(progress);
              progressItem.status = progress === 100 ? 'Upload complete!' : 'Uploading...';
            });
          } catch (err) {
            progressItem.status = `Error: ${err.message}`;
            progressItem.error = true;
          }
        }

        // Reset form
        this.multipleUpload = {
          files: [],
          titles: [],
          descriptions: []
        };
        
        // Reset file input
        const fileInput = document.getElementById('files');
        if (fileInput) fileInput.value = '';

      } catch (err) {
        console.error('Error in multiple upload:', err);
      } finally {
        this.uploading = false;
      }
    },

    handleDragLeave(e) {
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },

    handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      this.dragCounter = 0;
      
      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('video/'));
      if (files.length > 0) {
        this.multipleUpload.files = [...this.multipleUpload.files, ...files];
        this.multipleUpload.titles = [...this.multipleUpload.titles, ...files.map(() => '')];
        this.multipleUpload.descriptions = [...this.multipleUpload.descriptions, ...files.map(() => '')];
      }
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background-color: #f5f7fa;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
  padding: 40px;
}

.video-uploader {
  max-width: 900px;
  margin: 0 auto;
}

.video-uploader h2 {
  font-size: 2.2rem;
  color: #1a1f36;
  margin-bottom: 30px;
  font-weight: 600;
}

.upload-form {
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.upload-methods {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  padding: 5px;
  background: #f5f7fa;
  border-radius: 12px;
  padding: 5px;
}

.method-btn {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.method-btn.active {
  background: #3b82f6;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.method-btn:hover:not(.active) {
  background: rgba(59, 130, 246, 0.1);
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #1a1f36;
  font-size: 0.95rem;
}

input[type="text"],
textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  color: #1a1f36;
}

input[type="text"]:focus,
textarea:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

textarea {
  height: 120px;
  resize: vertical;
  line-height: 1.5;
}

.upload-btn {
  background: #3b82f6;
  color: white;
  padding: 14px 28px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.upload-btn:not(:disabled):hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.upload-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  transform: none;
}

.selected-files {
  margin-top: 30px;
}

.selected-files h3 {
  font-size: 1.4rem;
  color: #1a1f36;
  margin-bottom: 20px;
  font-weight: 600;
}

.file-item {
  display: flex;
  align-items: flex-start;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 15px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.file-item:hover {
  border-color: #3b82f6;
  background: #f0f7ff;
}

.file-info {
  flex: 1;
  margin-right: 20px;
}

.file-name {
  color: #1a1f36;
  font-weight: 600;
  margin-bottom: 15px;
  display: block;
  font-size: 1.1rem;
}

.file-metadata {
  display: grid;
  gap: 15px;
  width: 100%;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.file-input {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.file-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.remove-btn {
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #dc2626;
  transform: translateY(-1px);
}

.upload-progress {
  margin-top: 30px;
  background: white;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.upload-progress h3 {
  font-size: 1.4rem;
  color: #1a1f36;
  margin-bottom: 20px;
  font-weight: 600;
}

.progress-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-title {
  font-weight: 500;
  color: #1a1f36;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.progress-fill.error {
  background: #ef4444;
}

.progress-percentage {
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
}

.progress-status {
  font-size: 0.9rem;
  color: #64748b;
}

.drop-zone {
  border: 3px dashed #e2e8f0;
  border-radius: 16px;
  padding: 50px 30px;
  text-align: center;
  transition: all 0.2s ease;
  background: #f8fafc;
  cursor: pointer;
  margin-bottom: 30px;
}

.drop-zone--over {
  border-color: #3b82f6;
  background: #f0f7ff;
}

.drop-zone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.drop-zone__icon {
  color: #64748b;
  width: 60px;
  height: 60px;
  transition: all 0.2s ease;
}

.drop-zone:hover .drop-zone__icon {
  color: #3b82f6;
  transform: translateY(-2px);
}

.drop-zone__text {
  margin: 0;
  color: #64748b;
  font-size: 1.1rem;
  line-height: 1.6;
}

.drop-zone__input {
  display: none;
}

.drop-zone__label {
  background: #3b82f6;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.drop-zone__label:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 20px;
  }

  .upload-form {
    padding: 20px;
  }

  .file-item {
    flex-direction: column;
  }

  .file-info {
    margin-right: 0;
    margin-bottom: 15px;
  }

  .remove-btn {
    width: 100%;
  }
}
</style> 